import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { subscribeNewsletter } from "../../services/api";
import { getAttribution } from "../ReferralTracker/ReferralTracker";
import { track } from "../../services/analytics";

export type NewsletterSignupVariant = "footer" | "inline" | "card";

export interface MagnetProps {
  slug: string;
  heading: string;
  description: string;
  ctaLabel: string;
  pdfUrl: string;
}

interface NewsletterSignupProps {
  source: string;
  variant: NewsletterSignupVariant;
  magnet?: MagnetProps;
}

type Status = "idle" | "loading" | "success" | "error";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const styles = `
  .newsletter-signup { position: relative; }
  .newsletter-signup .newsletter-signup-heading { margin-bottom: 8px; }
  .newsletter-signup .newsletter-signup-desc { opacity: 0.75; font-size: 14px; margin-bottom: 20px; }
  .newsletter-signup-form { display: flex; gap: 0; max-width: 420px; position: relative; }
  .newsletter-signup-form input[type="email"] {
    flex: 1;
    height: 54px;
    padding: 0 18px;
    border: 1px solid rgba(255,255,255,0.15);
    border-radius: 100px 0 0 100px;
    color: var(--color-heading, #fff);
    background: rgba(255,255,255,0.04);
    outline: none;
  }
  .newsletter-signup-form input[type="email"]:focus { border-color: var(--color-primary); }
  .newsletter-signup-form input[type="email"]::placeholder { color: #8a8b92; }
  .newsletter-signup-form button {
    height: 54px;
    padding: 0 26px;
    border: none;
    border-radius: 0 100px 100px 0;
    font-weight: 700;
    font-size: 14px;
    color: var(--color-secondary, #00020f);
    background: var(--color-primary);
    cursor: pointer;
    white-space: nowrap;
  }
  .newsletter-signup-form button:disabled { opacity: 0.6; cursor: not-allowed; }
  .newsletter-signup-form button:hover:not(:disabled) { background: var(--color-white, #fff); }
  .newsletter-signup-message { margin-top: 14px; font-size: 14px; }
  .newsletter-signup-message.success { color: var(--color-primary); }
  .newsletter-signup-message.error { color: #ff6b6b; }
  .newsletter-signup-honeypot {
    position: absolute; left: -9999px; width: 1px; height: 1px; opacity: 0; overflow: hidden;
  }
  .newsletter-signup--card {
    padding: 40px 45px;
    border-radius: 16px;
    background: rgba(0,255,151,0.05);
    border: 1px solid rgba(0,255,151,0.2);
  }
  .newsletter-signup-microcopy { margin-top: 10px; font-size: 12px; opacity: 0.6; }

  /* Footer variant — centred to match the rest of the footer (wordmark, nav and
     legal links are all centred). Left-aligned it sat flush at x=0 with no
     gutter, reading as a layout bug rather than a CTA. */
  .newsletter-signup--footer {
    text-align: center;
    max-width: 660px;
    margin: 0 auto;
    padding: 0 20px;
  }
  .newsletter-signup--footer .newsletter-signup-heading {
    font-size: clamp(22px, 2.4vw, 32px);
    line-height: 1.25;
    margin-bottom: 12px;
  }
  .newsletter-signup--footer .newsletter-signup-desc {
    font-size: 16px;
    margin-bottom: 26px;
  }
  .newsletter-signup--footer .newsletter-signup-form {
    max-width: 520px;
    margin: 0 auto;
  }
  .newsletter-signup--footer .newsletter-signup-form input[type="email"] {
    height: 60px;
    font-size: 16px;
  }
  .newsletter-signup--footer .newsletter-signup-form button {
    height: 60px;
    padding: 0 34px;
    font-size: 15px;
  }

  @media (max-width: 767px) {
    .newsletter-signup--card { padding: 30px 24px; }
    .newsletter-signup-form { flex-direction: column; max-width: 100%; }
    .newsletter-signup-form input[type="email"] { border-radius: 100px; margin-bottom: 12px; }
    .newsletter-signup-form button { border-radius: 100px; }
    .newsletter-signup--footer .newsletter-signup-form { max-width: 100%; }
  }
`;

const NewsletterSignup: React.FC<NewsletterSignupProps> = ({ source, variant, magnet }) => {
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [website, setWebsite] = useState(""); // honeypot
  const [status, setStatus] = useState<Status>("idle");
  const [message, setMessage] = useState("");

  const submitHandler = async (e: React.FormEvent) => {
    e.preventDefault();

    if (website.trim() !== "") {
      // Honeypot tripped — pretend success, don't call the API.
      setStatus("success");
      setMessage(t("newsletter.success"));
      return;
    }

    if (!EMAIL_RE.test(email.trim())) {
      setStatus("error");
      setMessage(t("newsletter.errorInvalidEmail"));
      return;
    }

    setStatus("loading");
    setMessage("");

    try {
      const response = await subscribeNewsletter({
        email: email.trim(),
        source,
        ...getAttribution(),
      });
      setStatus("success");
      if (magnet) {
        setMessage(`Your kit is downloading in a new tab. ${response.message || ""}`.trim());
        window.open(magnet.pdfUrl, "_blank", "noopener,noreferrer");
        track("generate_lead", { source });
      } else {
        setMessage(response.message || t("newsletter.success"));
        track("newsletter_signup", { source });
      }
      setEmail("");
    } catch (err: unknown) {
      setStatus("error");
      setMessage(err instanceof Error ? err.message : t("newsletter.errorGeneric"));
    }
  };

  const wrapperClass = `newsletter-signup newsletter-signup--${variant}`;

  return (
    <div className={wrapperClass}>
      <style>{styles}</style>
      <h4 className="newsletter-signup-heading">{magnet ? magnet.heading : t("newsletter.heading")}</h4>
      <p className="newsletter-signup-desc">{magnet ? magnet.description : t("newsletter.description")}</p>

      {status === "success" ? (
        <p className="newsletter-signup-message success">{message}</p>
      ) : (
        <form className="newsletter-signup-form" onSubmit={submitHandler} noValidate>
          <input
            type="email"
            name="email"
            placeholder={t("newsletter.placeholder")}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            aria-label={t("newsletter.placeholder")}
          />
          <input
            type="text"
            name="website"
            className="newsletter-signup-honeypot"
            value={website}
            onChange={(e) => setWebsite(e.target.value)}
            tabIndex={-1}
            autoComplete="off"
            aria-hidden="true"
          />
          <button type="submit" disabled={status === "loading"}>
            {status === "loading" ? t("newsletter.submitting") : magnet ? magnet.ctaLabel : t("newsletter.submit")}
          </button>
        </form>
      )}
      {magnet && status !== "success" && (
        <p className="newsletter-signup-microcopy">Free PDF. No credit card. Delivered instantly.</p>
      )}

      {status === "error" && <p className="newsletter-signup-message error">{message}</p>}
    </div>
  );
};

export default NewsletterSignup;
